import axios, { Method } from "axios";
import { AxiosRequestConfig } from "axios";
import router from "../router";

import { ApiSearch } from "@lab/types/api";
import { UserResponse, UserDetails } from "@/shared/types/users";

axios.defaults.baseURL = process.env.VUE_APP_SERVICES;

// Intercept the request to make sure the token is injected into the header.
if (window.localStorage) {
	axios.interceptors.request.use((config) => {
		if (window.localStorage.getItem("token")) {
			config.headers.Authorization =
				"BEARER " + window.localStorage.getItem("token");
			// testing for IE10 or 11 to add a random string to help with a known IE cache bug
			// https://forum.vuejs.org/t/vue-js-in-ie-11-reactivity-issue-array-doesnt-update/10177
			if (/Trident\/|MSIE/.test(window.navigator.userAgent)) {
				config.params = {
					rand: new Date().getTime()
				};
			}
		}
		return config;
	});
}

axios.interceptors.response.use(
	(resp) => resp,
	(err) => {
		// Redirect to login on token expiry
		if (err.response && [401, 403].includes(err.response.status)) {
			if (window.location.pathname !== "/login") {
				router.push({
					path: "/login",
					query: {
						redirect: window.location.pathname
					}
				});
			}
			return;
		}
		return Promise.reject(err);
	}
);

export function login(
	email: string,
	password: string,
	rememberMe: boolean
): Promise<UserResponse | undefined> {
	const data = {
		email,
		password,
		rememberMe
	};

	return axios.post("/auth/login/admin", data, {}).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data as UserResponse;
	});
}

export function become(userID: number): Promise<UserResponse | undefined> {
	const data = {
		userID
	};

	return axios
		.post("/useradmin/become/" + userID, data, {})
		.then((response) => {
			if (response.status >= 400) {
				throw response;
			}
			if (!response.data) {
				return undefined;
			}
			return response.data as UserResponse;
		});
}

export function currentUserDetails(): Promise<UserDetails | undefined> {
	return axios.get("/users/details").then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data as UserDetails;
	});
}

export function updatePassword(userid: number, password: string) {
	const data = {
		id: userid,
		password
	};
	return axios
		.post(`/users/${userid}/password`, data, {})
		.then((response) => {
			if (response.status >= 400) {
				throw response;
			}
			if (!response.data) {
				return undefined;
			}
			return response.data;
		});
}

export function resetPassword(email: string) {
	const data = email || {};

	return axios.post("/auth/resetpassword", data, {}).then((response) => {
		const status = response.status;
		if (status >= 400) {
			throw response;
		}
		if (status >= 200 && status < 300) {
			return response.data || true;
		}
		return response.data;
	});
}

export function validToken(userid: number, token: string) {
	// Make sure userid is a number
	userid = Number(userid);
	const data = {
		userid,
		token
	};

	return axios.post("/auth/validtoken", data, {}).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function invite(invitation) {
	const data = invitation || {};

	return axios.post("/auth/invite", data, {}).then((response) => {
		const status = response.status;
		if (status >= 400) {
			throw response;
		}
		if (status >= 200 && status < 300) {
			return response.data || true;
		}
		return response.data;
	});
}

export function setPassword(userid: number, token: string, password: string) {
	const data = {
		userid,
		token,
		password
	};
	return axios.post("/auth/setpassword", data, {}).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function confirmInvite(
	userid: number,
	token: string,
	password: string,
	firstname: string,
	lastname: string
) {
	const data = {
		userid,
		token,
		password,
		firstname,
		lastname
	};
	return axios.post("/auth/invite", data, {}).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function resendInvite(userid: number) {
	return axios.post(`/users/${userid}/resendinvite`).then((response) => {
		const status = response.status;
		if (status >= 400) {
			throw response;
		}
		if (status >= 200 && status < 300) {
			return response.data || true;
		}
		return response.data;
	});
}

export function getInvites(userid: number) {
	return axios.get(`/users/${userid}/invites`).then((response) => {
		const status = response.status;
		if (status >= 400) {
			throw response;
		}
		if (status >= 200 && status < 300) {
			return response.data || true;
		}
		return response.data;
	});
}

export function organisations(method: Method | "get", data: any) {
	const opts: AxiosRequestConfig = {
		method,
		url: "/organisations",
		data: data || {},
		withCredentials: true
	};

	if (data && data.id) {
		opts.url = opts.url + "/" + data.id;
	}

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function hospitalUsers(
	method: Method | "get",
	data: any,
	search: ApiSearch
) {
	const { hospitalID } = search;
	const opts: AxiosRequestConfig = {
		method,
		url: "/hospitals/" + hospitalID + "/users",
		data: data || {},
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function userAssignToHospital(
	method: Method | "get",
	{ userID, hospitalID }
) {
	const opts: AxiosRequestConfig = {
		method,
		url: `/hospitals/${hospitalID.toString()}/user/${userID.toString()}`
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function organisationUsers(
	method: Method | "get",
	data,
	search: ApiSearch
) {
	const { organisationID } = search;
	const opts: AxiosRequestConfig = {
		method: method || "get",
		url: "/organisations/" + organisationID + "/users",
		data: data || {},
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function getUserListingsPerformance(userID, start, end) {
	const opts: AxiosRequestConfig = {
		method: "post",
		url: `/metrics/users/${userID.toString()}/listingsperformanceextended/table?start=${start}&end=${end}`
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function getUserShortlistReferralData(userID, start, end) {
	const opts: AxiosRequestConfig = {
		method: "post",
		url: `/metrics/users/${userID.toString()}/shortlists/table?start=${start}&end=${end}`
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function getUserListingsHeatSource(userID, start, end) {
	const opts: AxiosRequestConfig = {
		method: "post",
		url: `metrics/users/${userID}/hotsuburbs/mapdata?start=${start}&end=${end}`
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function getGlobalReports() {
	const opts: AxiosRequestConfig = {
		method: "get",
		url: "/metrics/globalreports/list"
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function users(method: Method | "get", data, search) {
	const url =
		search && search.organisationID
			? `/organisations/${search.organisationID}/users`
			: "/users"; // eslint-disable-line

	const opts = {
		method: method || "get",
		url,
		data: data || {},
		withCredentials: true
	};

	if (data && data.id) {
		opts.url = opts.url + "/" + data.id;
	}

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function userAssignToOrg(
	method: Method | "get",
	{ userID, organisationID }
) {
	const opts: AxiosRequestConfig = {
		method,
		url: `/organisations/${organisationID.toString()}/user/${userID.toString()}`
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function getUserOrganisation(userID) {
	const opts: AxiosRequestConfig = {
		method: "get",
		url: `/users/${userID.toString()}/organisation`
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function getUserLastSeen(userID) {
	const opts: AxiosRequestConfig = {
		method: "get",
		url: `/users/${userID.toString()}/lastseen`
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function getUserHospital(userID) {
	const opts: AxiosRequestConfig = {
		method: "get",
		url: `/users/${userID.toString()}/hospital`
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function listingsListShort() {
	const opts: AxiosRequestConfig = {
		method: "get",
		url: "/listings/short",
		data: {},
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function userBookingListings(
	method: Method | "get",
	data,
	search: ApiSearch
) {
	const opts: AxiosRequestConfig = {
		method: method || "get",
		url: "/bookinglistings/",
		data: data || {},
		withCredentials: true
	};

	if (data && data.id) {
		opts.url = `${opts.url}${data.id}`;
	}

	if (search && search.userid) {
		opts.url = `/bookinglistings/users/${search.userid}/list`;
	}

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function bookings(method: Method | "get", data) {
	const opts = {
		method: method || "get",
		url: "/bookings",
		data: data || {},
		withCredentials: true
	};

	if (data && data.id) {
		opts.url = opts.url + "/" + data.id;
	}

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function userListings(method: Method | "get", data, search) {
	const { userid } = search;
	const opts = {
		method: method || "get",
		url: "/users/" + userid + "/listings",
		data: data || {},
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function userDashboards(method: Method | "get", data, search) {
	const { userid } = search;
	const opts = {
		method: method || "get",
		url: "/users/" + userid + "/dashboards",
		data: data || {},
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function userVacancytypes(method: Method | "get", data, search) {
	const { userid } = search;
	const opts = {
		method: method || "get",
		url: "/users/" + userid + "/vacancytypes",
		data: data || {},
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function userGroups(method: Method | "get", data, search) {
	const { userid } = search;
	const opts = {
		method: method || "get",
		url: "/users/" + userid + "/groups",
		data: data || {},
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function userAssignToDashboard(
	method: Method | "get",
	{ userID, dashboardID }
) {
	const opts = {
		method,
		url: `/dashboards/${dashboardID.toString()}/user/${userID.toString()}`
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function getUserDashboard(userID) {
	const opts: AxiosRequestConfig = {
		method: "get",
		url: `/users/${userID.toString()}/dashboards`
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function inviteUsers(data) {
	const opts: AxiosRequestConfig = {
		method: "post",
		url: "/users/invite/access",
		data: data || {},
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function inviteHospitalUsers(data) {
	const opts: AxiosRequestConfig = {
		method: "post",
		url: "/users/invite/connect",
		data: data || {},
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function exportListingUsers(listingID) {
	const opts: AxiosRequestConfig = {
		method: "get",
		url: `/listings/${listingID}/users.csv`,
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function exportListingVacancy({ organisationid, listingids }) {
	const opts: AxiosRequestConfig = {
		method: "post",
		responseType: "blob",
		url: `/organisations/${organisationid}/listings/vacancyemail.pdf`,
		withCredentials: true,
		data: listingids || []
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function exportListingCompactVacancy({ organisationid, listingids }) {
	const opts: AxiosRequestConfig = {
		method: "post",
		responseType: "blob",
		url: `/organisations/${organisationid}/listings/vacancyemail.pdf?compact=1`,
		withCredentials: true,
		data: listingids || []
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function exportOrganisationUsers(organisationID) {
	const opts: AxiosRequestConfig = {
		method: "get",
		url: `/organisations/${organisationID}/users.csv`,
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function rooms(method: Method | "get", data) {
	const opts = {
		method: method || "get",
		url: "/rooms",
		data: data || {},
		withCredentials: true
	};

	if (data && data.id) {
		opts.url = opts.url + "/" + data.id;
	}

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function services(method: Method | "get", data) {
	const opts = {
		method: method || "get",
		url: "/services",
		data: data || {},
		withCredentials: true
	};

	if (data && data.id) {
		opts.url = opts.url + "/" + data.id;
	}

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function listings(method: Method | "get", data) {
	const opts = {
		method: method || "get",
		url: "/listings",
		data: data || {},
		withCredentials: true
	};

	// We need to unset the contactUser and Emailuser
	if (method === "PUT" || method === "POST") {
		data.contactuser = undefined;
	}

	if (data && data.id) {
		opts.url = opts.url + "/" + data.id;
	}

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function listingUpdateMedia(listingID, type, media = []) {
	const opts: AxiosRequestConfig = {
		method: "post",
		url: `/listings/${listingID}/${type}`,
		data: media,
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function organisationListings(method: Method | "get", data, search) {
	const { organisationID } = search;
	const opts = {
		method: method || "get",
		url: "/organisations/" + organisationID + "/listings",
		data: data || {},
		withCredentials: true
	};

	if (data && data.id) {
		opts.url = opts.url + "/" + data.id;
	}

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function listingRooms(method: Method | "get", data, search) {
	const { listingID } = search;
	const opts = {
		method: method || "get",
		url: "/listings/" + listingID + "/rooms",
		data: data || {},
		withCredentials: true
	};

	// if (data && data['id']) { 	opts.url = opts.url + '/' + data['id']; }

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function listingUsers(method: Method | "get", data, search) {
	const { listingID } = search;
	const opts = {
		method: method || "get",
		url: "/listings/" + listingID + "/users",
		data: data || {},
		withCredentials: true
	};

	// if (data && data['id']) { 	opts.url = opts.url + '/' + data['id']; }

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function listingNotificationGroup(method: Method | "get", data, search) {
	const { listingID } = search;
	const opts = {
		method: method || "get",
		url: "/listings/" + listingID + "/notifications",
		data: data || {},
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function notifications(method: Method | "get", notification) {
	const { id } = notification;
	const opts = {
		method: method || "get",
		url: `/notifications/${id}`,
		data: notification || {},
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function listingShortlist(method: Method | "get", data, search) {
	const { listingID } = search;
	const opts = {
		method: method || "get",
		url: "/listings/" + listingID + "/shortlists",
		data: data || {},
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function vacancytypes(method: Method | "get", data, search: ApiSearch) {
	let url = "vacancytypes";
	if (search) {
		const { listingID } = search;
		if (listingID) {
			url = `listings/${listingID}/vacancytypes`;
		}
	}

	const opts = {
		method: method || "get",
		url,
		data: data || {},
		withCredentials: true
	};

	if (data && data.id) {
		opts.url = opts.url + "/" + data.id;
	}

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function vacancytypesSetCurrent(listingID) {
	const opts: AxiosRequestConfig = {
		method: "post",
		url: `/listings/${listingID}/vacancytypes/current`,
		data: {},
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function get(url) {
	const opts: AxiosRequestConfig = {
		method: "get",
		url,
		withCredentials: true
	};

	return axios(opts).then((response) => {
		return response.data;
	});
}

export function register(method: Method | "get", url, data, token) {
	const opts: AxiosRequestConfig = {
		method: method || "post",
		url,
		data: data || {},
		withCredentials: false
	};

	if (token) {
		opts.withCredentials = true;
		opts.headers = {
			Authorization: "BEARER " + token
		};
		// if we have an id number attached to the data we are editing. so need to
		// update the url to suite.
		if (data && data.id) {
			opts.url = opts.url + "/" + data.id;
		}
	}

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function homecareproviders(method: Method | "get", data) {
	const opts = {
		method: method || "get",
		url: "/homecareproviders",
		data: data || {},
		withCredentials: true
	};
	if (data && data.id) {
		opts.url = opts.url + "/" + data.id;
	}
	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function healthservices(method: Method | "get", data) {
	const opts = {
		method: method || "get",
		url: "/healthservices",
		data: data || {},
		withCredentials: true
	};
	if (data && data.id) {
		opts.url = opts.url + "/" + data.id;
	}
	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function hospitals(method: Method | "get", data) {
	const opts = {
		method: method || "get",
		url: "/hospitals",
		data: data || {},
		withCredentials: true
	};
	if (data && data.id) {
		opts.url = opts.url + "/" + data.id;
	}
	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function hospitalCatchments(hospital) {
	const { id, catchments } = hospital;

	const opts: AxiosRequestConfig = {
		method: "post",
		url: "/hospitals/" + id + "/catchments",
		data: catchments || [],
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function hospitalGroups(method: Method | "get", data) {
	const { hospitalid } = data;
	const url = hospitalid ? `hospitals/${hospitalid}/groups` : "groups";

	const opts = {
		method: method || "get",
		url,
		data: data || {},
		withCredentials: true
	};

	if (data && data.id) {
		opts.url = opts.url + "/" + data.id;
	}

	// Remove user ids
	if (data && data.users && data.users.length > 0) {
		data.users = [];
	}

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function hospitalGroupUsers(method: Method | "get", user) {
	const { hospitalid, groupid, userid } = user;

	const opts = {
		method: method || "post",
		url: `/hospitals/${hospitalid}/groups/${groupid}/user/${userid}`,
		data: {},
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function suburbSearch(query, limit) {
	const opts: AxiosRequestConfig = {
		method: "get",
		url: `/suburbs/${query}/${limit || 20}`,
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function suburbRadiusSearch(data) {
	const { suburb, postcode, radius } = data;
	const opts: AxiosRequestConfig = {
		method: "get",
		url: `/suburbs/radius/${suburb}/${postcode}/${radius}`,
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function suburbStateSearch(data) {
	const { state } = data;
	const opts: AxiosRequestConfig = {
		method: "get",
		url: `/suburbs/state/${state}`,
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function hospitalAutoEmail(hospital) {
	const opts: AxiosRequestConfig = {
		method: "post",
		url: `/autoemails/`,
		data: hospital,
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}

export function loadAutoEmails(method: Method | "get", autoemail) {
	const { id } = autoemail;
	const opts = {
		method: method || "post",
		url: `/autoemails/hospitalcatchment/${id || ""}`,
		data: autoemail,
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}
export function autoEmails(method: Method | "get", autoemail) {
	const { id } = autoemail;
	const opts: AxiosRequestConfig = {
		method: id ? "put" : "post",
		url: `/autoemails/${id || ""}`,
		data: autoemail,
		withCredentials: true
	};

	return axios(opts).then((response) => {
		if (response.status >= 400) {
			throw response;
		}
		if (!response.data) {
			return undefined;
		}
		return response.data;
	});
}
